/**
 * Well geometry and falling-block timing. Gravity is implicit: a block's row is
 * a pure function of (spawn time, fall time, now), moving in discrete
 * 1/stepsPerRow row steps (Tetris-like), resting one row interval on its
 * landing row (lock delay) before it locks.
 */
import { clamp01 } from '../math/stats.ts';
import type { ActiveBlock, GameConfig, GameState } from '../types.ts';

/**
 * Float tolerance for step quantization: `t / stepMs` for a t that is an exact
 * multiple of the step can land a hair below the integer.
 */
const STEP_EPS = 1e-9;

/** Time the block takes to fall one row (ms). */
export function rowIntervalMs(fallTimeMs: number, config: GameConfig): number {
  return fallTimeMs / config.wellRows;
}

/** Row (0 = top) where a block spawned over `bricks` layers comes to rest. */
export function landingRowFor(bricks: number, config: GameConfig): number {
  return config.wellRows - 1 - bricks;
}

/**
 * Engine time at which a block locks: it falls to its landing row, then rests
 * for one row interval. = spawnedAt + (wellRows − bricksAtSpawn) × rowInterval.
 */
export function lockTime(spawnedAt: number, fallTimeMs: number, bricksAtSpawn: number, config: GameConfig): number {
  return spawnedAt + (config.wellRows - bricksAtSpawn) * rowIntervalMs(fallTimeMs, config);
}

/** Quantized row of a block at `now`: 0 at spawn, capped at its landing row. */
export function rowOfBlock(block: ActiveBlock, config: GameConfig, now: number): number {
  const stepMs = rowIntervalMs(block.fallTimeMs, config) / config.stepsPerRow;
  const elapsed = Math.max(0, now - block.spawnedAt);
  const steps = Math.floor(elapsed / stepMs + STEP_EPS);
  return Math.min(steps / config.stepsPerRow, landingRowFor(block.bricksAtSpawn, config));
}

/** Fraction of the block's available time left: 1 at spawn → 0 at lock. */
export function timeLeftOfBlock(block: ActiveBlock, now: number): number {
  const total = block.lockAt - block.spawnedAt;
  return total > 0 ? clamp01((block.lockAt - now) / total) : 0;
}

/** Row of the active block (0 = top), quantized to gravity steps. 0 when no block is falling. */
export function blockRow(state: GameState, now: number): number {
  return state.active ? rowOfBlock(state.active, state.config, now) : 0;
}

/**
 * Row the active block will land on: wellRows − 1 − bricksAtSpawn. With no
 * active block, the row the next block would land on over the current stack.
 */
export function landingRow(state: GameState): number {
  return landingRowFor(state.active ? state.active.bricksAtSpawn : state.bricks, state.config);
}

/** Fraction of the active block's time remaining (1 at spawn → 0 at lock); 0 when no block is falling. */
export function timeLeftFraction(state: GameState, now: number): number {
  return state.active ? timeLeftOfBlock(state.active, now) : 0;
}
